import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, DollarSign, FileText, Lock, CreditCard } from './shared';

export default function EscrowTab() {
  const [escrows, setEscrows] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [drawBuyer, setDrawBuyer] = useState('');
  const [drawSeller, setDrawSeller] = useState('');
  const [drawAmount, setDrawAmount] = useState('');
  const [drawDesc, setDrawDesc] = useState('');
  const [drawHours, setDrawHours] = useState('72');
  const [drawLoading, setDrawLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);

  const refresh = useCallback(() => {
    fetch(`${API}/api/agent-credit/escrow/active`).then(r => r.json()).then(d => setEscrows(d.escrows || [])).catch(() => {});
    fetch(`${API}/api/agent-credit/escrow/revenue/stats`).then(r => r.json()).then(setRevenue).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const creditDraw = async (e) => {
    e.preventDefault();
    setDrawLoading(true); setActionResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/escrow/draw`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_address: drawBuyer.trim(), seller_address: drawSeller.trim(), amount: parseFloat(drawAmount), task_description: drawDesc.trim(), deadline_hours: parseInt(drawHours) }),
      });
      const data = await res.json();
      setActionResult(res.ok ? { type: 'success', msg: data.message } : { type: 'error', msg: data.detail });
      if (res.ok) refresh();
    } catch { setActionResult({ type: 'error', msg: 'Network error' }); }
    finally { setDrawLoading(false); }
  };

  const statusColors = {
    funded: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    submitted: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    settled: 'bg-green-500/10 text-green-400 border-green-500/20',
    rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
    defaulted: 'bg-red-500/10 text-red-400 border-red-500/20',
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Origination Revenue" value={`$${(revenue?.total_protocol_revenue || 0).toFixed(2)}`} sub={`${revenue?.origination_fee_pct || 0.5}% per draw`} color="green" />
        <StatCard icon={FileText} label="Credit Draws" value={revenue?.total_credit_draws || 0} color="blue" />
        <StatCard icon={Lock} label="Active Escrows" value={escrows.length} color="orange" />
      </div>

      <div className="rounded-2xl border border-blue-500/20 bg-blue-500/[0.03] p-6 mb-8">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><CreditCard className="w-5 h-5 text-blue-400" /> Credit Draw (Create Escrow)</h3>
        <form onSubmit={creditDraw} className="grid md:grid-cols-2 gap-4">
          <Field label="Buyer Agent Address"><input value={drawBuyer} onChange={e => setDrawBuyer(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="escrow-buyer-address" /></Field>
          <Field label="Seller Agent Address"><input value={drawSeller} onChange={e => setDrawSeller(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="escrow-seller-address" /></Field>
          <Field label="Amount (USD)"><input value={drawAmount} onChange={e => setDrawAmount(e.target.value)} placeholder="500" type="number" min="0" step="0.01" className={inputCls} required data-testid="escrow-amount" /></Field>
          <Field label="Deadline (hours)"><input value={drawHours} onChange={e => setDrawHours(e.target.value)} placeholder="72" type="number" min="1" className={inputCls} data-testid="escrow-deadline" /></Field>
          <div className="md:col-span-2">
            <Field label="Task Description"><input value={drawDesc} onChange={e => setDrawDesc(e.target.value)} placeholder="Describe the task..." className={inputCls.replace('font-mono','')} required data-testid="escrow-description" /></Field>
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={drawLoading} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:brightness-110" data-testid="escrow-draw-btn">
              {drawLoading ? 'Creating...' : 'Draw Credit into Escrow'}
            </Button>
          </div>
        </form>
      </div>

      {actionResult && (
        <div className={`mb-6 p-4 rounded-lg border text-sm ${actionResult.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="escrow-action-result">
          {actionResult.msg}
        </div>
      )}

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="escrows-table">
        <div className="px-5 py-4 border-b border-white/5"><h3 className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-blue-400" /> Active Escrows</h3></div>
        {escrows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-gray-500 uppercase border-b border-white/5">
                <th className="px-4 py-3 text-left">Buyer</th><th className="px-4 py-3 text-left">Seller</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-center">Status</th><th className="px-4 py-3 text-right">Deadline</th>
              </tr></thead>
              <tbody className="divide-y divide-white/[0.03]">
                {escrows.map(esc => (
                  <tr key={esc.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-mono text-xs">{esc.buyer_address?.slice(0,8)}...</td>
                    <td className="px-4 py-3 font-mono text-xs">{esc.seller_address?.slice(0,8)}...</td>
                    <td className="px-4 py-3 text-right font-mono">${esc.amount?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center"><span className={`text-xs px-2 py-0.5 rounded-full border ${statusColors[esc.status] || ''}`}>{esc.status}</span></td>
                    <td className="px-4 py-3 text-right text-xs text-gray-500">{new Date(esc.deadline).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center"><Lock className="w-8 h-8 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-600">No active escrows.</p></div>
        )}
      </div>
    </div>
  );
}
